
import React, { useState } from 'react';
import { Settings, Server, Key, Save, X, Database, CheckCircle, AlertCircle, Activity, Loader2, StickyNote, ShieldCheck, Terminal, Play, Bot } from 'lucide-react';
import { AppSettings, DbConfig, NotesDbConfig, AgentRole } from '../types';

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  onSave: (newSettings: AppSettings) => void;
  pendingMigrationSQL?: string;
  onClearMigration?: () => void;
}

const DEFAULT_DB_CONFIG: DbConfig = {
  host: '10.10.14.2', 
  port: '5432',
  database: 'cns_primary',
  user: 'devpath_user',
  password: '',
  schema: 'devpath_cpm'
};

const DEFAULT_NOTES_CONFIG: NotesDbConfig = {
  host: '10.10.14.2',
  port: '5432',
  database: 'claude_conversations',
  user: 'claude_user',
  password: ''
};

const SettingsDialog: React.FC<SettingsDialogProps> = ({ isOpen, onClose, settings, onSave, pendingMigrationSQL, onClearMigration }) => {
  const [formData, setFormData] = useState<AppSettings>({
    ...settings,
    dbConfig: settings.dbConfig || DEFAULT_DB_CONFIG,
    notesDbConfig: settings.notesDbConfig || DEFAULT_NOTES_CONFIG,
    agentApiKeys: settings.agentApiKeys || {}
  });
  const [activeTab, setActiveTab] = useState<'general' | 'roles' | 'database' | 'notes' | 'admin'>('general');
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [schemaStatus, setSchemaStatus] = useState<'idle' | 'running' | 'success' | 'error'>('idle');

  // Sync formData with settings prop when dialog opens
  React.useEffect(() => {
      if (isOpen) {
          setFormData({
              ...settings,
              dbConfig: settings.dbConfig || DEFAULT_DB_CONFIG,
              notesDbConfig: settings.notesDbConfig || DEFAULT_NOTES_CONFIG,
              agentApiKeys: settings.agentApiKeys || {}
          });
      }
  }, [isOpen, settings]);

  // If a pending migration exists, default to admin tab on open
  React.useEffect(() => {
      if (isOpen && pendingMigrationSQL) {
          setActiveTab('admin');
      }
  }, [isOpen, pendingMigrationSQL]);

  if (!isOpen) return null;

  const handleDbChange = (field: keyof DbConfig, value: string) => {
    setFormData(prev => ({
      ...prev,
      dbConfig: { ...prev.dbConfig!, [field]: value }
    }));
    setTestStatus('idle');
  };

  const handleNotesDbChange = (field: keyof NotesDbConfig, value: string) => {
    setFormData(prev => ({
      ...prev,
      notesDbConfig: { ...prev.notesDbConfig!, [field]: value }
    }));
    setTestStatus('idle');
  };

  const handleApiKeyChange = (role: string, value: string) => {
      setFormData(prev => ({
          ...prev,
          agentApiKeys: {
              ...prev.agentApiKeys,
              [role]: value
          }
      }));
  };

  const testDbConnection = async (type: 'main' | 'notes') => {
    if (!formData.useBackend) {
      setTestStatus('error');
      setTestMessage("Enable Remote Backend Mode to test database connection.");
      return;
    }

    setTestStatus('testing');
    try {
      const endpoint = type === 'main' ? '/api/test-db' : '/api/bridge/test-notes';
      const payload = type === 'main' ? formData.dbConfig : formData.notesDbConfig;

      const response = await fetch(`${formData.backendUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setTestStatus('success');
        setTestMessage(data.message);
      } else {
        setTestStatus('error');
        setTestMessage(data.error || "Connection failed");
      }
    } catch (err: any) {
      setTestStatus('error');
      setTestMessage(err.message || "Network error contacting KVM4");
    }
  };

  const applySchemaUpdates = async (isDynamic: boolean) => {
     setSchemaStatus('running');
     try {
        const endpoint = isDynamic ? '/api/admin/execute-sql' : '/api/admin/apply-schema';
        const payload = isDynamic 
            ? { dbConfig: formData.dbConfig, sql: pendingMigrationSQL } 
            : formData.dbConfig;

        const response = await fetch(`${formData.backendUrl}${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (data.success) {
            setSchemaStatus('success');
            setTestMessage(data.message);
            if (isDynamic && onClearMigration) {
                onClearMigration();
            }
        } else {
            setSchemaStatus('error');
            setTestMessage(data.error);
        }
     } catch (err: any) {
         setSchemaStatus('error');
         setTestMessage(err.message);
     }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-800">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-400" />
            System Configuration
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800 overflow-x-auto">
          <button onClick={() => setActiveTab('general')} className={`flex-1 min-w-[80px] py-3 text-xs font-medium transition-colors border-b-2 ${activeTab === 'general' ? 'border-indigo-500 text-white bg-slate-800/30' : 'border-transparent text-slate-400'}`}>General</button>
          <button onClick={() => setActiveTab('roles')} className={`flex-1 min-w-[80px] py-3 text-xs font-medium transition-colors border-b-2 ${activeTab === 'roles' ? 'border-purple-500 text-white bg-slate-800/30' : 'border-transparent text-slate-400'}`}>AI Roles & Keys</button>
          <button onClick={() => setActiveTab('database')} className={`flex-1 min-w-[80px] py-3 text-xs font-medium transition-colors border-b-2 ${activeTab === 'database' ? 'border-emerald-500 text-white bg-slate-800/30' : 'border-transparent text-slate-400'}`}>Project DB</button>
          <button onClick={() => setActiveTab('notes')} className={`flex-1 min-w-[80px] py-3 text-xs font-medium transition-colors border-b-2 ${activeTab === 'notes' ? 'border-amber-500 text-white bg-slate-800/30' : 'border-transparent text-slate-400'}`}>Notes DB</button>
          <button onClick={() => setActiveTab('admin')} className={`flex-1 min-w-[80px] py-3 text-xs font-medium transition-colors border-b-2 ${activeTab === 'admin' ? 'border-red-500 text-white bg-slate-800/30' : 'border-transparent text-slate-400'}`}>
             Admin 
             {pendingMigrationSQL && <span className="ml-2 w-2 h-2 inline-block bg-red-500 rounded-full animate-pulse"></span>}
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          
          {/* GENERAL TAB */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="bg-slate-800/50 p-4 rounded-lg space-y-3">
                 <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="radio" 
                      name="mode" 
                      checked={!formData.useBackend} 
                      onChange={() => setFormData(prev => ({ ...prev, useBackend: false }))}
                      className="w-4 h-4 text-indigo-500 bg-slate-700 border-slate-600 focus:ring-indigo-600 focus:ring-2"
                    />
                    <div className="flex-1">
                       <div className="text-sm font-medium text-white flex items-center gap-2">
                          <Key className="w-4 h-4 text-indigo-400" /> Client-Side Mode
                       </div>
                       <p className="text-xs text-slate-400">Direct Gemini API calls. No Database access.</p>
                    </div>
                 </label>

                 <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="radio" 
                      name="mode" 
                      checked={formData.useBackend} 
                      onChange={() => setFormData(prev => ({ ...prev, useBackend: true }))}
                      className="w-4 h-4 text-indigo-500 bg-slate-700 border-slate-600 focus:ring-indigo-600 focus:ring-2"
                    />
                    <div className="flex-1">
                       <div className="text-sm font-medium text-white flex items-center gap-2">
                          <Server className="w-4 h-4 text-orange-400" /> Remote Backend Mode
                       </div>
                       <p className="text-xs text-slate-400">Connects to KVM4 Flask API. Enables Database features.</p>
                    </div>
                 </label>
              </div>

              {formData.useBackend ? (
                 <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1">BACKEND URL (KVM4)</label>
                    <input 
                      type="text" 
                      value={formData.backendUrl}
                      onChange={(e) => setFormData(prev => ({ ...prev, backendUrl: e.target.value }))}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none"
                    />
                 </div>
              ) : (
                <div className="p-3 bg-slate-800 rounded-lg text-sm text-slate-300 border border-slate-700">
                    <p className="font-semibold text-white mb-1">Environment API Key</p>
                    <p>Client-side mode uses <code>process.env.API_KEY</code> provided at build/runtime.</p>
                 </div>
              )}
            </div>
          )}

          {/* AI ROLES TAB */}
          {activeTab === 'roles' && (
              <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-purple-400 bg-purple-900/10 p-3 rounded border border-purple-900/30 mb-4">
                    <Bot className="w-4 h-4" />
                    <p>Assign specific API keys to roles to separate billing/usage. Leave blank to use default.</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                      {/* SW ARCHITECT */}
                      <div className="bg-slate-800/50 p-4 rounded border border-indigo-900/50">
                          <div className="flex justify-between items-center mb-2">
                              <div>
                                  <h4 className="text-white font-semibold text-sm">Software Architect (Google_SW_Architect)</h4>
                                  <p className="text-[10px] text-slate-400">Gemini 3 Pro • High Budget (4096)</p>
                              </div>
                              <span className="text-[9px] bg-indigo-600 px-2 py-0.5 rounded text-white">High Value</span>
                          </div>
                          <input 
                            type="password" 
                            placeholder="Optional: Paste specific API Key for this role"
                            value={formData.agentApiKeys?.[AgentRole.SW_ARCHITECT] || ''}
                            onChange={(e) => handleApiKeyChange(AgentRole.SW_ARCHITECT, e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-indigo-500"
                          />
                      </div>

                      {/* STRATEGIST */}
                      <div className="bg-slate-800/50 p-4 rounded border border-purple-900/50">
                          <div className="flex justify-between items-center mb-2">
                              <div>
                                  <h4 className="text-white font-semibold text-sm">Chief Strategist</h4>
                                  <p className="text-[10px] text-slate-400">Gemini 3 Pro • Max Budget (16384)</p>
                              </div>
                              <span className="text-[9px] bg-purple-600 px-2 py-0.5 rounded text-white">High Value</span>
                          </div>
                          <input 
                            type="password" 
                            placeholder="Optional: Paste specific API Key for this role"
                            value={formData.agentApiKeys?.[AgentRole.STRATEGIST] || ''}
                            onChange={(e) => handleApiKeyChange(AgentRole.STRATEGIST, e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-purple-500"
                          />
                      </div>

                      {/* DATA MINER */}
                      <div className="bg-slate-800/50 p-4 rounded border border-slate-700">
                          <div className="flex justify-between items-center mb-2">
                              <div>
                                  <h4 className="text-white font-semibold text-sm">Data Miner (Bulk Ops)</h4>
                                  <p className="text-[10px] text-slate-400">Gemini 2.0 Flash • Fast/Cheap</p>
                              </div>
                              <span className="text-[9px] bg-slate-600 px-2 py-0.5 rounded text-white">Standard</span>
                          </div>
                          <input 
                            type="password" 
                            placeholder="Optional: Paste specific API Key for this role"
                            value={formData.agentApiKeys?.[AgentRole.DATA_MINER] || ''}
                            onChange={(e) => handleApiKeyChange(AgentRole.DATA_MINER, e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-slate-500"
                          />
                      </div>
                  </div>
              </div>
          )}

          {/* PROJECT DATABASE TAB */}
          {activeTab === 'database' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-800/30 p-3 rounded border border-slate-700">
                <Database className="w-4 h-4" />
                <p>Connection to CNS Primary (Project Storage).</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Host</label>
                  <input type="text" value={formData.dbConfig?.host} onChange={(e) => handleDbChange('host', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Port</label>
                  <input type="text" value={formData.dbConfig?.port} onChange={(e) => handleDbChange('port', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none" />
                </div>
              </div>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Database</label>
                  <input type="text" value={formData.dbConfig?.database} onChange={(e) => handleDbChange('database', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Schema</label>
                  <input type="text" value={formData.dbConfig?.schema} onChange={(e) => handleDbChange('schema', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">User</label>
                  <input type="text" value={formData.dbConfig?.user} onChange={(e) => handleDbChange('user', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Password</label>
                  <input type="password" value={formData.dbConfig?.password} onChange={(e) => handleDbChange('password', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none" />
                </div>
              </div>
              <button onClick={() => testDbConnection('main')} className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">
                 {testStatus === 'testing' ? <Loader2 className="w-4 h-4 animate-spin"/> : <Activity className="w-4 h-4" />} Test Project DB
              </button>
            </div>
          )}

          {/* NOTES DATABASE TAB */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-amber-500/80 bg-amber-900/10 p-3 rounded border border-amber-900/30">
                <StickyNote className="w-4 h-4" />
                <p>Connection to Raw Notes Pool (Read-Only).</p>
              </div>

               <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Host</label>
                  <input type="text" value={formData.notesDbConfig?.host} onChange={(e) => handleNotesDbChange('host', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Port</label>
                  <input type="text" value={formData.notesDbConfig?.port} onChange={(e) => handleNotesDbChange('port', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Database</label>
                  <input type="text" value={formData.notesDbConfig?.database} onChange={(e) => handleNotesDbChange('database', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
                </div>
                 <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">User</label>
                  <input type="text" value={formData.notesDbConfig?.user} onChange={(e) => handleNotesDbChange('user', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Password</label>
                  <input type="password" value={formData.notesDbConfig?.password} onChange={(e) => handleNotesDbChange('password', e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
                </div>
              </div>
               <button onClick={() => testDbConnection('notes')} className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">
                 {testStatus === 'testing' ? <Loader2 className="w-4 h-4 animate-spin"/> : <Activity className="w-4 h-4" />} Test Notes DB
              </button>
            </div>
          )}

          {/* ADMIN TAB */}
          {activeTab === 'admin' && (
             <div className="space-y-6">
                 {/* Pending Dynamic Migration (from AI) */}
                 {pendingMigrationSQL ? (
                     <div className="bg-red-900/20 border border-red-500/50 p-4 rounded-lg animate-fade-in">
                        <h3 className="text-red-300 font-semibold flex items-center gap-2 mb-2">
                             <AlertCircle className="w-5 h-5" /> Pending AI Proposal
                        </h3>
                        <p className="text-xs text-slate-400 mb-3">
                            The DB Architect has proposed the following SQL migration. Review carefully before executing.
                        </p>
                        <div className="bg-black/50 p-3 rounded border border-red-900/30 font-mono text-xs text-red-200 whitespace-pre-wrap mb-4">
                            {pendingMigrationSQL}
                        </div>
                        <div className="flex gap-3">
                             <button 
                                onClick={() => applySchemaUpdates(true)}
                                disabled={schemaStatus === 'running'}
                                className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded-lg font-medium text-sm flex justify-center items-center gap-2"
                             >
                                {schemaStatus === 'running' ? <Loader2 className="w-4 h-4 animate-spin"/> : <Play className="w-4 h-4" />}
                                Execute on KVM8
                             </button>
                             <button 
                                onClick={onClearMigration}
                                className="px-4 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm"
                             >
                                Discard
                             </button>
                        </div>
                     </div>
                 ) : (
                     <div className="p-4 border border-dashed border-slate-700 rounded-lg text-center text-slate-500 text-sm">
                         No dynamic migrations pending from AI.
                     </div>
                 )}

                 <div className="border-t border-slate-800 pt-6">
                    <h3 className="text-white font-semibold flex items-center gap-2 mb-2">
                        <ShieldCheck className="w-5 h-5 text-slate-400" /> Static Maintenance
                    </h3>
                    <p className="text-xs text-slate-400 mb-4">
                        Apply standard schema updates from <code>schema_updates.sql</code>.
                    </p>
                    
                    <button 
                        onClick={() => applySchemaUpdates(false)}
                        disabled={schemaStatus === 'running' || !formData.useBackend}
                        className="w-full bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white py-2 rounded-lg font-medium text-sm transition-colors flex justify-center items-center gap-2"
                    >
                         {schemaStatus === 'running' ? <Loader2 className="w-4 h-4 animate-spin"/> : <Database className="w-4 h-4" />}
                         Apply Static Schema
                    </button>
                 </div>
             </div>
          )}

           {/* Status Message */}
           {testMessage && (
               <div className={`mt-4 p-2 rounded text-xs text-center ${
                   (testStatus === 'success' || schemaStatus === 'success') ? 'text-emerald-400 bg-emerald-900/20' : 'text-red-400 bg-red-900/20'
               }`}>
                 {testMessage}
               </div>
            )}

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-800 bg-slate-900/50 backdrop-blur">
          <button 
            onClick={() => { onSave(formData); onClose(); }}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" /> Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsDialog;
